import { useState } from "react";

import { ToggleInput, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { JntSheet } from "@/modules/settings/components/JntSheet";
import {
  useJntConnectionQuery,
  useUpdateJntActiveMutation,
} from "@/modules/settings/services/useJntConnection";
import { IntegrationTile } from "./IntegrationTile";
import { JntLogo } from "./logos/JntLogo";

type Props = {
  storeSlug: string;
};

export function JntIntegrationTile({ storeSlug }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { connection, platformEnabled, isLoading } = useJntConnectionQuery({
    storeSlug,
  });
  const { mutateAsync: updateActive, isPending: isTogglingActive } =
    useUpdateJntActiveMutation();
  const [sheetOpen, setSheetOpen] = useState(false);

  // Platform kill-switch: hide J&T entirely when the admin disabled it.
  if (!platformEnabled) {
    return null;
  }

  const status = isLoading
    ? "loading"
    : !connection
      ? "disconnected"
      : connection.isActive
        ? "connected"
        : "paused";

  const statusLabel = !connection
    ? t(keys.jnt.statusNotConnected)
    : connection.isActive
      ? t(keys.jnt.statusConnected)
      : t(keys.jnt.statusPaused);

  const handleToggle = async (next: boolean) => {
    try {
      await updateActive({ storeSlug, isActive: next });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  const footer = connection ? (
    <>
      <span className="text-xs text-[color:var(--text-secondary)]">
        {connection.isActive
          ? t(keys.jnt.pauseAction)
          : t(keys.jnt.resumeAction)}
      </span>
      <ToggleInput
        name="jnt-active"
        checked={connection.isActive}
        disabled={isTogglingActive}
        onChange={(event) => handleToggle(event.target.checked)}
        ariaLabel={
          connection.isActive
            ? t(keys.jnt.pauseAction)
            : t(keys.jnt.resumeAction)
        }
      />
    </>
  ) : undefined;

  return (
    <>
      <IntegrationTile
        name={t(keys.jnt.brandName)}
        description={t(keys.jnt.description)}
        logo={<JntLogo />}
        status={status}
        statusLabel={statusLabel}
        onClick={() => setSheetOpen(true)}
        footer={footer}
      />
      {sheetOpen ? (
        <JntSheet
          storeSlug={storeSlug}
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
        />
      ) : null}
    </>
  );
}
